/**
 * Login Component
 *
 * Displays the login interface with Okta sign-in functionality and IP registration information.
 * Supports responsive design with different messaging for mobile and desktop views.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.loginOnclick - Callback function triggered when the Log In button is clicked
 * @returns {React.ReactElement} The rendered login page with logo, notification, and login button
 *
 * @example
 * // Basic usage
 * <Login loginOnclick={handleLoginClick} />
 *
 * Features:
 * - Responsive notification text for mobile and desktop views
 * - Loading state management for the login button
 * - American Express branding with logo
 * - Legal disclaimer and copyright information
 */

import { useState } from 'react'
import { Button, PageLevelNotification, Surface } from '@americanexpress/dls-react'
import AppLogo from './common/AppLogo'
import styles from './scss/Login.module.scss'

export default function Login({ loginOnclick }) {
  const [isLoginBtnLoading, toggleLoginBtnLoader] = useState(false)

  return (
    <>
      <div className={styles.loginContainer}>
        <Surface data-dls-mode='light'>
          <AppLogo />

          <PageLevelNotification
            className={styles.pageLevelNotificationWrapper}
            isDismissible={false}
            variant='compact'
            data-dls-mode='light'
          >
            <p>
              Sign in with Okta. New IPs auto-register; inactive IPs need Re-register. For E1/E2
              destination, use Submit E1/E2 Destination in the user menu.
            </p>
          </PageLevelNotification>
        </Surface>

        <Button
          onClick={() => {
            toggleLoginBtnLoader(true)
            loginOnclick()
          }}
          isLoading={isLoginBtnLoading}
          disabled={isLoginBtnLoading}
        >
          Log In
        </Button>
      </div>

      <p>
        This site is owned by American Express and is restricted to authorized users only. Any
        unauthorized access may result in personal liability, and/or criminal prosecution.
      </p>
      <p>{`\u00A9 ${new Date().getFullYear()} American Express Company. All Rights Reserved`}</p>
    </>
  )
}
